import React from 'react';
import { Link } from 'react-router-dom';
import { Cookie, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

import usePageSeo from '../hooks/usePageSeo';
const CookiePolicy = () => {
  usePageSeo({
    title: 'Cookie Policy | WPAA',
    description: 'How White Paper Accounts Auditing uses cookies and similar technologies on www.wpaauae.com.',
    path: '/cookie-policy'
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="pt-32 pb-12 px-4 bg-gradient-to-br from-teal-900 via-teal-800 to-gray-900 text-white">
        <div className="container mx-auto">
          <div className="flex items-center space-x-2 mb-4">
            <Cookie className="text-teal-400" size={28} />
            <span className="text-teal-400 font-semibold text-sm uppercase tracking-wider">Legal</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Cookie Policy</h1>
          <p className="text-gray-300">Last updated: February 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
            
            <div className="prose prose-lg max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. What Are Cookies?</h2>
              <p className="text-gray-600 mb-6">
                Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use our site.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Types of Cookies We Use</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Essential Cookies</h3>
              <p className="text-gray-600 mb-4">
                These cookies are necessary for the website to function properly. They enable core functionality such as security, network management, and accessibility. You cannot opt out of these cookies.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Analytics Cookies</h3>
              <p className="text-gray-600 mb-4">
                These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website&apos;s performance and user experience.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Functional Cookies</h3>
              <p className="text-gray-600 mb-4">
                These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings. They may be set by us or by third-party providers whose services we use.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Marketing Cookies</h3>
              <p className="text-gray-600 mb-6">
                These cookies may be set through our site by our advertising partners. They may be used to build a profile of your interests and show you relevant advertisements on other sites.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How to Manage Cookies</h2>
              <p className="text-gray-600 mb-4">
                You can control and manage cookies in various ways:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                <li><strong>Browser Settings:</strong> Most browsers allow you to refuse or accept cookies, delete existing cookies, and set preferences for certain websites.</li>
                <li><strong>Opt-out Links:</strong> Some third-party cookies can be opted out of through industry opt-out platforms.</li>
                <li><strong>Device Settings:</strong> Your device may have settings that control how cookies are used.</li>
              </ul>
              <p className="text-gray-600 mb-6">
                Please note that disabling certain cookies may affect the functionality of our website and your user experience.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Third-Party Cookies</h2>
              <p className="text-gray-600 mb-4">
                We may use third-party services that set their own cookies, such as analytics providers and social media platforms. We do not control these third-party cookies and recommend reviewing their respective privacy policies.
              </p>
              <p className="text-gray-600 mb-4">
                Specifically, our website currently uses the following third-party tools:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                <li>
                  <strong>Google Analytics 4 &amp; Google Ads:</strong> for measuring website traffic, conversions and advertising performance. IP addresses are anonymised where possible. Read Google&apos;s privacy notice at{' '}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">policies.google.com/privacy</a>.
                </li>
                <li>
                  <strong>Microsoft Clarity:</strong> we use Microsoft Clarity to capture how visitors interact with our site (mouse movements, clicks, scrolls). All personal information in forms is automatically masked. Read more at{' '}
                  <a href="https://clarity.microsoft.com" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">clarity.microsoft.com</a>.
                </li>
                <li>
                  <strong>Google Tag Manager:</strong> used to manage the analytics and advertising tags listed above. Read more at{' '}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">policies.google.com/privacy</a>.
                </li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Updates to This Policy</h2>
              <p className="text-gray-600 mb-6">
                We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We encourage you to review this policy periodically.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Contact Us</h2>
              <p className="text-gray-600 mb-6">
                If you have questions about our use of cookies, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <p className="text-gray-700"><strong>White Paper Accounts Auditing</strong></p>
                <p className="text-gray-600">109, Midaffa Bldg., Musab Bin Omair St.</p>
                <p className="text-gray-600">Al Mahatta, Sharjah, UAE</p>
                <p className="text-gray-600">Email: info@wpaauae.com</p>
                <p className="text-gray-600">Phone: +971 50 121 9341</p>
                <p className="text-gray-600">Office: +971 6 744 7940</p>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t">
              <Link to="/">
                <Button variant="outline" className="flex items-center space-x-2">
                  <ArrowLeft size={18} />
                  <span>Back to Home</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicy;
